/**
 * Lightweight toast queue. Components push toasts via `pushToast(...)`;
 * the <Toaster /> in Layout renders the queue and auto-dismisses each
 * after its `durationMs`. No external dep — Radix's toast was overkill
 * for the few notification points the app needs.
 */
import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  durationMs: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number }) => string;
  dismiss: (id: string) => void;
}

let toastIdSeq = 0;
function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Non-secure-context fallback — a monotonic counter avoids same-millisecond
  // key collisions that would dupe React keys / mis-target dismiss().
  toastIdSeq += 1;
  return `t-${String(Date.now())}-${String(toastIdSeq)}-${Math.random().toString(36).slice(2)}`;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: ({ variant, title, description, durationMs = 4000 }) => {
    const id = genId();
    const toast: Toast =
      description === undefined
        ? { id, variant, title, durationMs }
        : { id, variant, title, description, durationMs };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience helpers so callers don't need to import the store directly. */
export function pushToast(
  toast: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number },
): string {
  return useToastStore.getState().push(toast);
}

export function dismissToast(id: string): void {
  useToastStore.getState().dismiss(id);
}
